import { yupResolver } from "@hookform/resolvers/yup";
import { CustomerType } from "@omni/types/api/customer/bff/shared/enums/customerType.enum";
import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import { ComponentsCustomerType } from "@omni/types/components/customer/shared/customerType.enum";
import { cn } from "@omni/utils/styles";
import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";

import { BonusCardField } from "@/components/Form/RHF/FormFields/BonusCardField/bonusCardField.component";
import { OptionalBonusCardFieldSchema } from "@/components/Form/RHF/FormFields/BonusCardField/bonusCardField.schema";
import { DateOfBirthField } from "@/components/Form/RHF/FormFields/DateOfBirthField/dateOfBirthField.component";
import { OptionalDateOfBirthFieldSchema } from "@/components/Form/RHF/FormFields/DateOfBirthField/dateOfBirthField.schema";
import { PhoneField } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.container";
import { RequiredPhoneSchema } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.schema";
import { RadioFieldArray } from "@/components/Form/RHF/FormFields/RadioFieldArray/radioFieldArray.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { VatField } from "@/components/Form/RHF/FormFields/VatField/vatField.container";
import { ConditionalyRequiredVatSchema } from "@/components/Form/RHF/FormFields/VatField/vatField.schema";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { getVatPrefixes } from "@/redux/modules/api/data/vatPrefixes/vatPrefixes.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { safeParseISO } from "@/utils/date";
import { parseDateOfBirth } from "@/utils/dateOfBirth";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import * as RHFUserValidationSchema from "@/utils/validation/schemas/user.RHF";

const FORM_ID = "customerDetailsModal";

const EditProfileFormFormValidationSchema = yup
    .object({
        firstName: RHFUserValidationSchema.firstName,
        lastName: RHFUserValidationSchema.lastName
    })
    .concat(RequiredPhoneSchema)
    .concat(ConditionalyRequiredVatSchema)
    .concat(OptionalBonusCardFieldSchema)
    .concat(OptionalDateOfBirthFieldSchema);

export type EditProfileFormValues = yup.InferType<
    typeof EditProfileFormFormValidationSchema
>;

export type EditProfileFormProps = {
    customer: ComponentsCustomer;
    onSubmit: (formValues: EditProfileFormValues) => Promise<void>;
};

const EditProfileForm = ({ customer, onSubmit }: EditProfileFormProps) => {
    const { t } = useGranite();
    const [isB2B, setIsB2B] = useState(
        customer.customerType === ComponentsCustomerType.B2B
    );

    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );
    const vatPrefixes = useSelector(getVatPrefixes, shallowEqual);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            bonusCardNumber:
                customer.eBonusCardNumber || customer.bonusCardNumber || "",
            firstName: customer.firstName || "",
            lastName: customer.lastName || "",
            phone: customer.phone || "",
            phonePrefix: customer.phonePrefix || "+32",
            dateOfBirth: customer.dateOfBirth
                ? safeParseISO(customer.dateOfBirth)
                : null,
            customerType: customer.customerType || CustomerType.B2C,
            ...parseDateOfBirth(customer.dateOfBirth),
            vatInfo: {
                vatNr: customer.vatNr || "",
                vatNrPrefix: customer.vatNrPrefix || "BE",
                companyName: customer.companyName || ""
            }
        },
        resolver: yupResolver(EditProfileFormFormValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    const { subscribe } = methods;

    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                const shouldBeB2B =
                    values.customerType === ComponentsCustomerType.B2B;
                if (shouldBeB2B !== isB2B) {
                    setIsB2B(shouldBeB2B);
                }
            }
        });
        return () => callback();
    }, [subscribe, isB2B]);

    return (
        <>
            <Messages messageid={"customerDetails-feedback-failure"} />
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onSubmit)}
                        >
                            <div className={cn("tw:pt-4")}>
                                <Row>
                                    <Col>
                                        <RadioFieldArray<
                                            EditProfileFormValues,
                                            CustomerType
                                        >
                                            name="customerType"
                                            label={t(
                                                "general.form.customer.type"
                                            )}
                                            options={[
                                                {
                                                    value: CustomerType.B2C,
                                                    label: t(
                                                        "general.form.customerType.label.b2c"
                                                    )
                                                },
                                                {
                                                    value: CustomerType.B2B,
                                                    label: t(
                                                        "general.form.customerType.label.b2b"
                                                    )
                                                }
                                            ]}
                                            required
                                        />
                                    </Col>
                                </Row>
                                {isB2B && (
                                    <Row>
                                        <Col
                                            xs={12}
                                            md={6}
                                        >
                                            <VatField<EditProfileFormValues>
                                                label={t(
                                                    "general.form.vatNr.label"
                                                )}
                                                required
                                                vatPrefixes={vatPrefixes}
                                            />
                                        </Col>
                                        <Col
                                            xs={12}
                                            md={6}
                                        >
                                            <TextField<EditProfileFormValues>
                                                name="vatInfo.companyName"
                                                label={t(
                                                    "general.form.companyName.label"
                                                )}
                                                required
                                                autoComplete={"organization"}
                                                placeholder={t(
                                                    "general.form.companyName.placeholder"
                                                )}
                                            />
                                        </Col>
                                    </Row>
                                )}
                                <Row>
                                    <Col md={6}>
                                        <TextField<EditProfileFormValues>
                                            test="customerDetailsFormBlock-input-firstName"
                                            name="firstName"
                                            label={t(
                                                "general.form.firstName.label"
                                            )}
                                            required
                                            autoComplete={"given-name"}
                                            placeholder={t(
                                                "general.form.firstName.placeholder"
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <TextField<EditProfileFormValues>
                                            test="customerDetailsFormBlock-input-lastName"
                                            name="lastName"
                                            label={t(
                                                "general.form.lastName.label"
                                            )}
                                            required
                                            autoComplete={"family-name"}
                                            placeholder={t(
                                                "general.form.lastName.placeholder"
                                            )}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <PhoneField<EditProfileFormValues>
                                            test="customerDetailsFormBlock-input-phone"
                                            autoComplete={"tel-national"}
                                            label={t(
                                                "general.form.phoneNumber.label"
                                            )}
                                            placeholder={t(
                                                "general.form.phoneNumber.placeholder"
                                            )}
                                            required
                                            phonenumberPrefixes={
                                                phonenumberPrefixes
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <DateOfBirthField<EditProfileFormValues>
                                            required={false}
                                        />
                                    </Col>
                                </Row>
                                <Row className={cn("tw:mb-4")}>
                                    <Col md={6}>
                                        <BonusCardField<EditProfileFormValues>
                                            label={t(
                                                "general.form.bonusCard.label"
                                            )}
                                            placeholder={t(
                                                "general.form.bonusCard.placeholder"
                                            )}
                                            required={false}
                                            eBonusCardNumber={
                                                customer.eBonusCardNumber
                                            }
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </>
    );
};

export { EditProfileForm };
